import { budgetAlert, BudgetFeedback } from "./BudgetAlert";
import React, { useState, useEffect, useMemo } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Grid from "components/Grid/GridContainer.js";
import Col from "components/Grid/GridItem.js";
import Card from "components/Card/Card.js";
import CardHeader from "components/Card/CardHeader.js";
import CardBody from "components/Card/CardBody.js";
import Button from "components/CustomButtons/Button.js";
import { BudgetService } from "_services/budget.service";
import { useBudgetCategories, getItemCategoryKey } from "./budgetCategories";

const months = [
    { key: "10", name: "ต.ค.", q: "Q1" },
    { key: "11", name: "พ.ย.", q: "Q1" },
    { key: "12", name: "ธ.ค.", q: "Q1" },
    { key: "01", name: "ม.ค.", q: "Q2" },
    { key: "02", name: "ก.พ.", q: "Q2" },
    { key: "03", name: "มี.ค.", q: "Q2" },
    { key: "04", name: "เม.ย.", q: "Q3" },
    { key: "05", name: "พ.ค.", q: "Q3" },
    { key: "06", name: "มิ.ย.", q: "Q3" },
    { key: "07", name: "ก.ค.", q: "Q4" },
    { key: "08", name: "ส.ค.", q: "Q4" },
    { key: "09", name: "ก.ย.", q: "Q4" }
];

const useStyles = makeStyles({
    cardTitleWhite: { color: "#FFFFFF", margin: 0, fontWeight: "300", fontFamily: '"Sarabun", sans-serif' },
    activeMonthCell: { backgroundColor: "#00acc1 !important", color: "#fff !important", fontWeight: "bold", cursor: "pointer", textAlign: "center" },
    inactiveMonthCell: { cursor: "pointer", textAlign: "center", "&:hover": { backgroundColor: "#e0f7fa" } },
    unassignedBadge: { backgroundColor: "#ffebee", color: "#c62828", padding: "4px 8px", borderRadius: "4px", fontSize: "11px", fontWeight: "bold" }
});

const BudgetTimelinePlanner = ({ cycleId = 1, departmentId = 108 }) => {
    const { getCategoryLabel, loading: categoriesLoading, error: categoriesError } = useBudgetCategories();
    const classes = useStyles();
    const [items, setItems] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        loadData();
    }, [cycleId, departmentId]);

    const loadData = async () => {
        setIsLoading(true);
        try {
            const res = await BudgetService.getDepartmentBudget(cycleId, departmentId);
            if (res.data.success && res.data.data) {
                // ดึงรายการทั้งหมดทั้งจากโครงการและ Flat items
                const req = res.data.data;
                let allItems = [...(req.items || [])];
                if (req.projects) {
                    req.projects.forEach(p => {
                        if (p.items) allItems.push(...p.items);
                    });
                }
                setItems(Array.from(new Map(allItems.map(item => [item.itemKey, item])).values()));
            }
        } catch (error) {
            console.error("Load timeline data error:", error);
        } finally {
            setIsLoading(false);
        }
    };

    // เปลี่ยนเดือนของแต่ละรายการ
    const handleSelectMonth = (itemKey, monthName) => {
        setItems(prev => prev.map(item => {
            if (item.itemKey === itemKey) {
                return { ...item, targetMonth: item.targetMonth === monthName ? "" : monthName };
            }
            return item;
        }));
    };

    // บันทึกการจัดสรร Timeline
    const handleSaveTimeline = async () => {
        setIsSaving(true);
        try {
            const payload = items.map(i => ({
                itemKey: i.itemKey,
                targetMonth: i.targetMonth || ""
            }));
            const res = await BudgetService.updateBatchTargetMonth(payload);
            if (res.data.success) {
                await budgetAlert.success("บันทึกแผนไทม์ไลน์การจัดซื้อเรียบร้อยแล้ว");
            }
        } catch (error) {
            budgetAlert.error("เกิดข้อผิดพลาด: " + error.message);
        } finally {
            setIsSaving(false);
        }
    };

    // คำนวณยอดเงินรวมรายเดือนสำหรับทำ Mini-Chart
    const monthlySummary = useMemo(() => {
        const summary = {};
        months.forEach(m => { summary[m.name] = 0; });
        let unassignedTotal = 0;

        items.forEach(item => {
            const total = Number(item.totalAmount) || 0;
            if (item.targetMonth && summary[item.targetMonth] !== undefined) {
                summary[item.targetMonth] += total;
            } else {
                unassignedTotal += total;
            }
        });

        return { summary, unassignedTotal };
    }, [items]);

    if (categoriesError) return <BudgetFeedback message={categoriesError} />;
    if (categoriesLoading) return <div>กำลังโหลดหมวดงบประมาณ...</div>;

    const maxMonthlyBudget = Math.max(...Object.values(monthlySummary.summary), 1);

    return (
        <div>
            <Grid>
                <Col xs={12}>
                    <Card>
                        <CardHeader color="info" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <div>
                                <h4 className={classes.cardTitleWhite}>
                                    <i className="fas fa-calendar-alt"></i> แผนกำหนดเวลาจัดซื้อจัดจ้าง (Procurement Timeline)
                                </h4>
                                <p style={{ margin: 0, color: "rgba(255,255,255,0.8)", fontSize: "13px" }}>
                                    คลิกเลือกเดือนที่ต้องการจัดซื้อของแต่ละรายการตามปีงบประมาณ (ต.ค. - ก.ย.)
                                </p>
                            </div>
                            <Button color="success" onClick={handleSaveTimeline} disabled={isSaving}>
                                {isSaving ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-save"></i>} บันทึกแผนจัดซื้อ
                            </Button>
                        </CardHeader>
                        <CardBody>
                            {/* --- ส่วน Summary Chart แผนการใช้เงินรายเดือน --- */}
                            <div className="p-3 mb-4 bg-light border rounded">
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    <h6 className="font-weight-bold m-0 text-info">
                                        <i className="fas fa-chart-bar"></i> ภาพรวมการกระจายงบประมาณรายเดือน (บาท)
                                    </h6>
                                    {monthlySummary.unassignedTotal > 0 && (
                                        <span className={classes.unassignedBadge}>
                                            ยังไม่กำหนดเดือน: {monthlySummary.unassignedTotal.toLocaleString()} บาท
                                        </span>
                                    )}
                                </div>
                                <div className="row text-center pt-2" style={{ alignItems: "flex-end", minHeight: "100px" }}>
                                    {months.map(m => {
                                        const val = monthlySummary.summary[m.name];
                                        const heightPercent = Math.max((val / maxMonthlyBudget) * 80, 5);
                                        return (
                                            <div key={m.key} className="col p-1">
                                                <div style={{ fontSize: "10px", color: val > 0 ? "#006064" : "#9e9e9e", fontWeight: "bold" }}>
                                                    {val > 0 ? (val / 1000).toFixed(0) + "k" : "-"}
                                                </div>
                                                <div 
                                                    style={{ 
                                                        height: `${val > 0 ? heightPercent : 4}px`, 
                                                        backgroundColor: val > 0 ? "#00acc1" : "#e0e0e0", 
                                                        borderRadius: "3px 3px 0 0",
                                                        transition: "height 0.3s"
                                                    }} 
                                                />
                                                <div className="font-weight-bold mt-1" style={{ fontSize: "11px" }}>{m.name}</div>
                                                <div className="text-muted" style={{ fontSize: "9px" }}>{m.q}</div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* --- ตาราง Interactive Timeline Grid --- */}
                            {isLoading ? (
                                <p className="text-center p-4">กำลังโหลดข้อมูลรายการ...</p>
                            ) : items.length === 0 ? (
                                <p className="text-center p-4">ไม่มีรายการคำขอในระบบ</p>
                            ) : (
                                <div className="table-responsive">
                                    <table className="table table-bordered" style={{ fontSize: "12px" }}>
                                        <thead className="thead-dark text-center">
                                            <tr>
                                                <th width="40" rowSpan="2">#</th>
                                                <th rowSpan="2" style={{ minWidth: "220px" }}>รายการ</th>
                                                <th width="100" rowSpan="2">งบประมาณ (บาท)</th>
                                                <th colSpan="3" style={{ backgroundColor: "#37474f" }}>ไตรมาส 1</th>
                                                <th colSpan="3" style={{ backgroundColor: "#455a64" }}>ไตรมาส 2</th>
                                                <th colSpan="3" style={{ backgroundColor: "#37474f" }}>ไตรมาส 3</th>
                                                <th colSpan="3" style={{ backgroundColor: "#455a64" }}>ไตรมาส 4</th>
                                            </tr>
                                            <tr>
                                                {months.map(m => (
                                                    <th key={m.key} width="45" style={{ fontSize: "11px" }}>{m.name}</th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {items.map((item, idx) => (
                                                <tr key={item.itemKey}>
                                                    <td className="text-center">{idx + 1}</td>
                                                    <td>
                                                        <strong>{item.itemName}</strong>
                                                        <div className="text-muted" style={{ fontSize: "11px" }}>
                                                            {getCategoryLabel(getItemCategoryKey(item))} ({item.requestQty} หน่วย)
                                                        </div>
                                                    </td>
                                                    <td className="text-right font-weight-bold text-info">
                                                        {Number(item.totalAmount).toLocaleString()}
                                                    </td>
                                                    {months.map(m => {
                                                        const isSelected = item.targetMonth === m.name;
                                                        return (
                                                            <td
                                                                key={m.key}
                                                                className={isSelected ? classes.activeMonthCell : classes.inactiveMonthCell}
                                                                onClick={() => handleSelectMonth(item.itemKey, m.name)}
                                                            >
                                                                {isSelected && <i className="fas fa-check"></i>}
                                                            </td>
                                                        );
                                                    })}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </CardBody>
                    </Card>
                </Col>
            </Grid>
        </div>
    );
};

export default BudgetTimelinePlanner;
